/**
 * Thumbnail hover-zoom — global listener that pops a larger preview when the
 * user mouses over any <img class="thumb-hover">.
 *
 * Single document-level mouseover/mouseout/mousemove handler so we don't have
 * to wire up per-row events. Preview is positioned near the cursor, flipped to
 * the opposite side if it'd overflow the viewport, and dismissed on mouseout,
 * scroll, or click.
 */

let _hoverEl: HTMLElement | null = null;
let _hoverTimer: number | null = null;
let _lastImg: HTMLImageElement | null = null;

const SHOW_DELAY_MS = 220;
const PAD = 14;
const MAX_W = 280;
const MAX_H = 400;

function buildPreview(src: string): HTMLElement {
  const div = document.createElement("div");
  div.id = "thumbHoverPreview";
  div.style.cssText = `
    position: fixed;
    z-index: 100;
    pointer-events: none;
    padding: 4px;
    background: var(--panel);
    border: 1px solid var(--border);
    border-radius: 8px;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.45);
    opacity: 0;
    transition: opacity 120ms ease-out;
  `;
  const img = document.createElement("img");
  img.src = src;
  img.alt = "";
  img.style.cssText = `
    display: block;
    height: auto;
    width: auto;
    max-width: ${MAX_W}px;
    max-height: ${MAX_H}px;
    object-fit: contain;
    border-radius: 4px;
  `;
  div.appendChild(img);
  return div;
}

function positionPreview(e: MouseEvent): void {
  if (!_hoverEl) return;
  const rect = _hoverEl.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let x = e.clientX + PAD;
  let y = e.clientY + PAD;
  if (x + rect.width  > vw - PAD) x = e.clientX - rect.width  - PAD;
  if (y + rect.height > vh - PAD) y = e.clientY - rect.height - PAD;
  if (x < PAD) x = PAD;
  if (y < PAD) y = PAD;
  _hoverEl.style.left = x + "px";
  _hoverEl.style.top  = y + "px";
}

function showPreview(src: string, e: MouseEvent): void {
  hidePreview();
  _hoverEl = buildPreview(src);
  document.body.appendChild(_hoverEl);
  positionPreview(e);
  requestAnimationFrame(() => { if (_hoverEl) _hoverEl.style.opacity = "1"; });
}

function hidePreview(): void {
  if (_hoverTimer !== null) { clearTimeout(_hoverTimer); _hoverTimer = null; }
  if (_hoverEl) { _hoverEl.remove(); _hoverEl = null; }
  _lastImg = null;
}

function isThumbImg(el: EventTarget | null): el is HTMLImageElement {
  return el instanceof HTMLImageElement && el.classList.contains("thumb-hover");
}

export function initThumbHover(): void {
  // mouseover fires when the target changes — including entering a child img.
  document.addEventListener("mouseover", (e) => {
    if (!isThumbImg(e.target)) return;
    const img = e.target;
    if (img === _lastImg) return; // already hovering this one
    _lastImg = img;
    const src = img.getAttribute("src");
    if (!src) return;
    if (_hoverTimer !== null) clearTimeout(_hoverTimer);
    // Capture cursor position now; the timer callback fires later.
    const cx = e.clientX;
    const cy = e.clientY;
    _hoverTimer = window.setTimeout(() => {
      _hoverTimer = null;
      showPreview(src, { clientX: cx, clientY: cy } as MouseEvent);
    }, SHOW_DELAY_MS);
  });
  document.addEventListener("mouseout", (e) => {
    if (!isThumbImg(e.target)) return;
    hidePreview();
  });
  // Follow the cursor while the preview is up.
  document.addEventListener("mousemove", (e) => {
    if (_hoverEl) positionPreview(e);
  });
  // Anything that re-flows the page invalidates the popover position; just hide.
  document.addEventListener("scroll", hidePreview, true);
  document.addEventListener("click", hidePreview, true);
  window.addEventListener("blur", hidePreview);
}
